import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faSpinner, faSearch, faFileInvoice,
    faCheck, faExclamationTriangle, faArrowLeft,
    faLink, faBan, faInfoCircle, faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import bankReconService from '../../services/bankReconService';
import SearchableSelect from '../Invoicing/SearchableSelect';
import './ManualMatchModal.css';
import { costCentreLabel } from '../../utils/costCentreLabel';

// Cost categories are group-wide (the viewset is explicitly not entity-scoped),
// so this picker takes no company filter.
const COST_CATEGORY_PARAMS = {};

const REASON_OPTIONS = [
    { value: 'bank_costs', label: 'Bank Costs', hint: 'Fees charged by the bank' },
    { value: 'finance_cost', label: 'Finance Cost', hint: 'Financing or factoring charges' },
    { value: 'loan', label: 'Loan', hint: 'Loan repayment or disbursement' },
    { value: 'interest', label: 'Interest', hint: 'Interest earned or paid' },
    { value: 'partial_payment', label: 'Partial Payment', hint: 'Invoice not fully settled' },
    { value: 'other', label: 'Other', hint: 'Specify a custom reason' },
];

const DOC_TYPE_LABELS = {
    outgoing_invoice: 'Outgoing Invoice',
    incoming_invoice: 'Incoming Invoice',
    credit_note: 'Credit Note',
    debit_note: 'Debit Note',
    proforma: 'Pro-Forma',
};

const ManualMatchModal = ({ statementId, hmsEntityId, hmsEntityName, line, onClose, onComplete }) => {
    const [mode, setMode] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [reason, setReason] = useState('');
    const [reasonDescription, setReasonDescription] = useState('');
    const [selectedGl, setSelectedGl] = useState('');
    const [selectedCostCenter, setSelectedCostCenter] = useState('');
    const [selectedCostCategory, setSelectedCostCategory] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const searchTimeout = useRef(null);
    const searchBoxRef = useRef(null);

    // Option A: the pickers offer this statement's company only. No silent
    // fallback to "every company" -- an unscoped chart is how a bank
    // difference lands in the wrong entity's books without anyone noticing.
    const glParams = { entity: hmsEntityId };
    const costCenterParams = { entity: hmsEntityId };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const doSearch = useCallback(async (q) => {
        if (!q || q.length < 2) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }
        setSearching(true);
        try {
            const res = await bankReconService.searchDocuments(statementId, line.id, q);
            setSearchResults(res.data || []);
            setDropdownOpen(true);
        } catch (err) {
            console.error('Search failed:', err);
        } finally {
            setSearching(false);
        }
    }, [statementId, line.id]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => doSearch(val), 350);
    };

    const handleSelectDoc = (doc) => {
        setSelectedDoc(doc);
        setDropdownOpen(false);
        setSearchQuery('');
        setSearchResults([]);
    };

    const handleClearDoc = () => {
        setSelectedDoc(null);
        setReason('');
        setReasonDescription('');
    };

    const formatAmount = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return '-';
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const resetMode = () => {
        setMode(null);
        setSelectedDoc(null);
        setSearchQuery('');
        setSearchResults([]);
        setDropdownOpen(false);
        setReason('');
        setReasonDescription('');
        setSelectedGl('');
        setSelectedCostCenter('');
        setSelectedCostCategory('');
        setError('');
    };

    // A partial payment is always against an invoice (Marcel, 28.09): picking it
    // on the no-invoice side opens the invoice search instead of a GL account.
    const handleReasonChange = (value) => {
        if (mode === 'no-document' && value === 'partial_payment') {
            setMode('document');
            setSelectedGl('');
            setSelectedCostCenter('');
            setSelectedCostCategory('');
            setError('');
        }
        setReason(value);
    };

    // After a part payment the invoice still owes the rest: compare the bank
    // line with what is OPEN, so the payment of the rest reads as exact.
    const docPaid = selectedDoc ? parseFloat(selectedDoc.amount_paid) || 0 : 0;
    const docOpen = (doc) => parseFloat(doc.open_amount ?? doc.document_amount) || 0;
    const bankAmount = Math.abs(parseFloat(line.signed_amount) || 0);
    const docAmount = selectedDoc ? docOpen(selectedDoc) : 0;
    const difference = mode === 'document' && selectedDoc ? (docAmount - bankAmount) : -bankAmount;
    const hasDifference = Math.abs(difference) > 0.005;
    const needsGlAccount = hasDifference && reason !== 'partial_payment';

    const handleSubmit = async () => {
        setError('');

        if (mode === 'document') {
            if (!selectedDoc) {
                setError('Please search for and select an invoicing object first.');
                return;
            }
            if (hasDifference && !reason) {
                setError('The bank amount and invoicing object amount differ. Please select a reason for the difference.');
                return;
            }
            if (needsGlAccount && !hmsEntityId) {
                setError('This statement has no IMT company assigned, so there is no chart of accounts to post to. Assign the company on the statement first.');
                return;
            }
            if (needsGlAccount && !selectedGl) {
                setError('A GL Account is required to post the amount difference.');
                return;
            }
        } else {
            if (!reason) {
                setError('Please select a reason for this transaction.');
                return;
            }
            if (!hmsEntityId) {
                setError('This statement has no IMT company assigned, so there is no chart of accounts to post to. Assign the company on the statement first.');
                return;
            }
            if (!selectedGl) {
                setError('A GL Account is required to post this transaction.');
                return;
            }
        }

        setSubmitting(true);
        try {
            if (mode === 'document') {
                await bankReconService.manualMatch(statementId, line.id, {
                    document_type: selectedDoc.document_type,
                    document_id: selectedDoc.document_id,
                    // A reason only means something when the amounts differ. A
                    // Partial Payment picked first and then matched to exactly
                    // what is open is the payment that settles the invoice.
                    reason: hasDifference ? reason : 'other',
                    reason_description: reasonDescription,
                    gl_account_id: selectedGl || null,
                    cost_center_id: selectedCostCenter || null,
                    cost_category_id: selectedCostCategory || null,
                });
            } else {
                await bankReconService.matchWithoutDocument(statementId, line.id, {
                    reason,
                    reason_description: reasonDescription,
                    gl_account_id: selectedGl,
                    cost_center_id: selectedCostCenter || null,
                    cost_category_id: selectedCostCategory || null,
                });
            }
            onComplete();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to create manual match.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="manual-match-modal" onClick={(e) => e.stopPropagation()}>
                <div className="md-modal-header manual-match-modal-header">
                    <div className="md-modal-header-text">
                        <FontAwesomeIcon icon={faFileInvoice} className="md-modal-header-icon" />
                        <h2>Manual Match -- Line #{line.line_number}</h2>
                    </div>
                    <button className="md-modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="md-modal-body manual-match-body">
                    {/* Bank line info */}
                    <div className="mm-bank-line-info">
                        <div className="mm-bank-line-info-title">Bank Transaction</div>
                        <div className="mm-info-row">
                            <span className="mm-info-label">Amount</span>
                            <span className={`mm-info-value ${line.credit_debit === 'CREDIT' ? 'mm-credit' : 'mm-debit'}`}>
                                {line.credit_debit === 'CREDIT' ? '+' : '-'}{formatAmount(line.amount)} {line.currency}
                            </span>
                        </div>
                        <div className="mm-info-row">
                            <span className="mm-info-label">Partner</span>
                            <span className="mm-info-value">{line.partner_name || '-'}</span>
                        </div>
                        <div className="mm-info-row">
                            <span className="mm-info-label">Description</span>
                            <span className="mm-info-value mm-desc-val">{line.description || line.reference || '-'}</span>
                        </div>
                    </div>

                    {/* Mode Selection */}
                    {!mode && (
                        <div className="mm-mode-selection">
                            <div className="mm-mode-selection-title">How would you like to match this transaction?</div>
                            <div className="mm-mode-cards">
                                <div className="mm-mode-card" onClick={() => setMode('document')}>
                                    <div className="mm-mode-card-icon">
                                        <FontAwesomeIcon icon={faLink} />
                                    </div>
                                    <div className="mm-mode-card-text">
                                        <h4>Link to an Invoicing Object</h4>
                                        <p>Search by partner name or invoice number to find the matching outgoing invoice, incoming invoice, credit note, debit note, or pro-forma that this bank transaction pays. The amount is recorded as paid; a partial payment leaves the rest open.</p>
                                    </div>
                                </div>
                                <div className="mm-mode-card" onClick={() => setMode('no-document')}>
                                    <div className="mm-mode-card-icon no-doc">
                                        <FontAwesomeIcon icon={faBan} />
                                    </div>
                                    <div className="mm-mode-card-text">
                                        <h4>No Matching Invoicing Object</h4>
                                        <p>This transaction has no corresponding invoice -- e.g. bank fees, interest, loan payments, or internal transfers. You'll assign a GL account to post it.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Back to mode selection */}
                    {mode && (
                        <button className="mm-back-to-modes" onClick={resetMode}>
                            <FontAwesomeIcon icon={faArrowLeft} /> Change matching type
                        </button>
                    )}

                    {/* MODE: Link to Invoicing Object */}
                    {mode === 'document' && (
                        <>
                            <div className="mm-mode-hint">
                                <FontAwesomeIcon icon={faInfoCircle} />
                                <span>
                                    {reason === 'partial_payment' && !selectedDoc
                                        ? 'A partial payment is always against an invoice. Search for it by partner name or invoice number; the amount is recorded as paid and the rest stays open.'
                                        : 'Search by partner name or invoice number to find the invoicing object this payment belongs to.'}
                                </span>
                            </div>

                            {/* Search with dropdown */}
                            {!selectedDoc && (
                                <div className="mm-search-wrapper" ref={searchBoxRef}>
                                    <div className="mm-search-box">
                                        <FontAwesomeIcon icon={faSearch} className="mm-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Type a partner name or invoice number..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            onFocus={() => { if (searchResults.length > 0) setDropdownOpen(true); }}
                                            className="mm-search-input"
                                            autoFocus
                                        />
                                        {searching && <FontAwesomeIcon icon={faSpinner} spin className="mm-search-spinner" />}
                                    </div>

                                    {dropdownOpen && (
                                        <div className="mm-search-dropdown">
                                            {searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
                                                <div className="mm-dropdown-empty">No invoicing objects found for "{searchQuery}"</div>
                                            )}
                                            {searchResults.map(doc => (
                                                <div
                                                    key={`${doc.document_type}-${doc.document_id}`}
                                                    className="mm-dropdown-item"
                                                    onClick={() => handleSelectDoc(doc)}
                                                >
                                                    <div className="mm-dropdown-item-left">
                                                        <span className="mm-dropdown-item-type">{DOC_TYPE_LABELS[doc.document_type] || doc.document_type}</span>
                                                        <span className="mm-dropdown-item-number">#{doc.document_number}</span>
                                                        <span className="mm-dropdown-item-partner">{doc.partner_name}</span>
                                                    </div>
                                                    <div className="mm-dropdown-item-right">
                                                        <span className="mm-dropdown-item-amount">
                                                            {parseFloat(doc.amount_paid) > 0
                                                                ? `Open ${formatAmount(docOpen(doc))} of ${formatAmount(doc.document_amount)} ${doc.currency}`
                                                                : `${formatAmount(doc.document_amount)} ${doc.currency}`}
                                                        </span>
                                                        <span className="mm-dropdown-item-status">{doc.status}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Selected invoicing object card */}
                            {selectedDoc && (
                                <div className="mm-selected-doc">
                                    <div className="mm-selected-doc-header">
                                        <span className="mm-selected-doc-label">Selected Invoicing Object</span>
                                        <button className="mm-selected-doc-clear" onClick={handleClearDoc} title="Change selection">
                                            <FontAwesomeIcon icon={faTimesCircle} /> Change
                                        </button>
                                    </div>
                                    <div className="mm-selected-doc-card">
                                        <div className="mm-selected-doc-top">
                                            <span className="mm-selected-doc-type">{DOC_TYPE_LABELS[selectedDoc.document_type] || selectedDoc.document_type}</span>
                                            <span className="mm-selected-doc-num">#{selectedDoc.document_number}</span>
                                        </div>
                                        <div className="mm-selected-doc-details">
                                            <div className="mm-selected-doc-row">
                                                <span>Partner</span>
                                                <span>{selectedDoc.partner_name}</span>
                                            </div>
                                            <div className="mm-selected-doc-row">
                                                <span>Amount</span>
                                                <span className="mm-selected-doc-amount">{formatAmount(selectedDoc.document_amount)} {selectedDoc.currency}</span>
                                            </div>
                                            {docPaid > 0 && (
                                                <>
                                                    <div className="mm-selected-doc-row">
                                                        <span>Paid so far</span>
                                                        <span>{formatAmount(docPaid)} {selectedDoc.currency}</span>
                                                    </div>
                                                    <div className="mm-selected-doc-row">
                                                        <span>Open</span>
                                                        <span className="mm-selected-doc-amount">{formatAmount(docAmount)} {selectedDoc.currency}</span>
                                                    </div>
                                                </>
                                            )}
                                            <div className="mm-selected-doc-row">
                                                <span>Status</span>
                                                <span className="mm-selected-doc-status">{selectedDoc.status}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Amount comparison */}
                                    <div className="mm-amount-compare">
                                        <div className="mm-compare-title">Amount Comparison</div>
                                        <div className="mm-compare-row">
                                            <span>Bank Amount:</span>
                                            <span>{formatAmount(bankAmount)} {line.currency}</span>
                                        </div>
                                        <div className="mm-compare-row">
                                            <span>{docPaid > 0 ? 'Open on Invoicing Object:' : 'Invoicing Object Amount:'}</span>
                                            <span>{formatAmount(docAmount)} {selectedDoc.currency}</span>
                                        </div>
                                        {hasDifference ? (
                                            <div className="mm-compare-row mm-compare-diff">
                                                <span>Difference:</span>
                                                <span>{formatAmount(difference)}</span>
                                            </div>
                                        ) : (
                                            <div className="mm-compare-row mm-compare-exact">
                                                <FontAwesomeIcon icon={faCheck} />
                                                <span>Amounts match exactly</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* MODE: No Invoicing Object */}
                    {mode === 'no-document' && (
                        <div className="mm-mode-hint">
                            <FontAwesomeIcon icon={faInfoCircle} />
                            <span>Select why this transaction has no invoicing object, then assign it to a GL account for proper bookkeeping.</span>
                        </div>
                    )}

                    {/* Reason selection */}
                    {(mode === 'no-document' || (mode === 'document' && selectedDoc && hasDifference)) && (
                        <div className="mm-reason-section">
                            <label className="mm-field-label">
                                {mode === 'no-document' ? 'What is this transaction for?' : 'Why do the amounts differ?'}
                            </label>
                            <div className="mm-reason-grid">
                                {REASON_OPTIONS.map(opt => (
                                    <label
                                        key={opt.value}
                                        className={`mm-reason-option ${reason === opt.value ? 'selected' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name="reason"
                                            value={opt.value}
                                            checked={reason === opt.value}
                                            onChange={() => handleReasonChange(opt.value)}
                                        />
                                        <div className="mm-reason-option-content">
                                            <span className="mm-reason-option-label">{opt.label}</span>
                                            <span className="mm-reason-option-hint">{opt.hint}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                            {reason === 'other' && (
                                <input
                                    type="text"
                                    className="mm-text-input"
                                    placeholder="Describe the reason..."
                                    value={reasonDescription}
                                    onChange={(e) => setReasonDescription(e.target.value)}
                                />
                            )}
                        </div>
                    )}

                    {/* GL Account, Cost Center, Cost Category */}
                    {(mode === 'no-document' || (mode === 'document' && selectedDoc && hasDifference && reason !== 'partial_payment')) && (
                        <div className="mm-gl-section">
                            <div className="mm-gl-section-title">
                                {mode === 'no-document'
                                    ? 'Where should this amount be posted?'
                                    : 'Where should the difference be posted?'
                                }
                            </div>
                            {/* No company on the statement means we cannot say
                                whose books this belongs in. Refuse to guess. */}
                            {!hmsEntityId ? (
                                <div className="mm-gl-unavailable">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <span>
                                        This statement has no IMT company assigned, so there is no
                                        chart of accounts to post to. Assign the company on the
                                        statement first.
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div className="mm-gl-row">
                                        <div className="mm-gl-field">
                                            <label className="mm-field-label">GL Account <span className="mm-required">*</span></label>
                                            {/* Scoped to the statement's own company: 70k+ accounts
                                                exist across the group, and posting a bank
                                                difference into another company's books is silent
                                                and wrong. Server-side search, 20 rows a page --
                                                the old <select> pulled every row (31.8 MB) and
                                                rendered them all as options. */}
                                            <SearchableSelect
                                                fetchUrl="/api/invoicing/gl-accounts/"
                                                params={glParams}
                                                displayFn={(gl) => `${gl.account_number} - ${gl.account_name}`}
                                                value={selectedGl}
                                                onChange={(id) => setSelectedGl(id || '')}
                                                placeholder="Search GL account by number or name..."
                                                id="mm_gl_account"
                                            />
                                            <span className="mm-gl-scope-hint">
                                                Chart of accounts for {hmsEntityName || 'this company'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mm-gl-row">
                                        <div className="mm-gl-field">
                                            <label className="mm-field-label">Cost Center <span className="mm-optional">(optional)</span></label>
                                            {/* listOnOpen: an operator knows the department, not
                                                the centre code, so an empty box with "start
                                                typing" would be a dead end. */}
                                            <SearchableSelect
                                                fetchUrl="/api/invoicing/cost-centers/"
                                                params={costCenterParams}
                                                displayFn={costCentreLabel}
                                                value={selectedCostCenter}
                                                onChange={(id) => setSelectedCostCenter(id || '')}
                                                placeholder="Search or browse cost centers..."
                                                listOnOpen
                                                id="mm_cost_center"
                                            />
                                        </div>
                                        <div className="mm-gl-field">
                                            <label className="mm-field-label">Cost Category <span className="mm-optional">(optional)</span></label>
                                            {/* Cost categories are group-wide, not per company --
                                                deliberately NOT entity-filtered. */}
                                            <SearchableSelect
                                                fetchUrl="/api/invoicing/cost-categories/"
                                                params={COST_CATEGORY_PARAMS}
                                                displayFn={(cat) => `${cat.code} - ${cat.short_name}`}
                                                value={selectedCostCategory}
                                                onChange={(id) => setSelectedCostCategory(id || '')}
                                                placeholder="Search or browse cost categories..."
                                                listOnOpen
                                                id="mm_cost_category"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="mm-error">
                            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
                        </div>
                    )}
                </div>

                <div className="md-modal-footer">
                    <button className="md-btn-cancel" onClick={onClose} disabled={submitting}>
                        Cancel
                    </button>
                    {mode && (
                        <button className="mm-submit-btn" onClick={handleSubmit} disabled={submitting}>
                            {submitting
                                ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
                                : <><FontAwesomeIcon icon={faCheck} /> Confirm Match</>
                            }
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualMatchModal;
